import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, ArrowLeft, Edit, Trash2, Search, Loader2, Wrench, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SparesDatabase = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [spares, setSpares] = useState([]);
  const [saving, setSaving] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingSpare, setEditingSpare] = useState(null);
  const [formData, setFormData] = useState({
    spare_name: "", category: "", description: "", default_quantity: 1, unit: "pcs", status: "Active",
  });

  useEffect(() => { fetchSpares(); }, []);

  const fetchSpares = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/spares-database`);
      setSpares(res.data);
    } catch (err) {
      toast.error("Failed to load spares database");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingSpare(null);
    setFormData({ spare_name: "", category: "", description: "", default_quantity: 1, unit: "pcs", status: "Active" });
    setShowDialog(true);
  };

  const handleEdit = (spare) => {
    setEditingSpare(spare);
    setFormData({
      spare_name: spare.spare_name || "",
      category: spare.category || "",
      description: spare.description || "",
      default_quantity: spare.default_quantity || 1,
      unit: spare.unit || "pcs",
      status: spare.status || "Active",
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.spare_name.trim()) { toast.error("Spare name is required"); return; }
    setSaving(true);
    try {
      if (editingSpare) {
        await axios.put(`${API}/spares-database/${editingSpare.id}`, formData);
        toast.success("Spare updated");
      } else {
        await axios.post(`${API}/spares-database`, formData);
        toast.success("Spare created");
      }
      setShowDialog(false);
      fetchSpares();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (spare) => {
    if (!window.confirm(`Delete spare "${spare.spare_name}"?`)) return;
    try {
      await axios.delete(`${API}/spares-database/${spare.id}`);
      toast.success("Spare deleted");
      fetchSpares();
    } catch (err) {
      toast.error("Failed to delete spare");
    }
  };

  const filtered = spares.filter((s) => {
    if (!searchFilter) return true;
    const q = searchFilter.toLowerCase();
    return (
      s.spare_name?.toLowerCase().includes(q) ||
      s.spare_id?.toLowerCase().includes(q) ||
      s.category?.toLowerCase().includes(q) ||
      s.description?.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Loader2 className="h-12 w-12 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/workorder/machines")} className="text-gray-400 hover:text-white" data-testid="back-to-machines">
              <ArrowLeft className="h-5 w-5 mr-2" /> Back
            </Button>
            <div className="flex items-center gap-3">
              <Wrench className="h-8 w-8 text-amber-400" />
              <h1 className="text-2xl font-bold text-white">Spares Database</h1>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card className="bg-gray-800/50 border border-amber-500/20">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-500/20 rounded-lg"><Wrench className="h-7 w-7 text-amber-400" /></div>
                <div>
                  <p className="text-2xl font-bold text-white">{spares.length}</p>
                  <p className="text-sm text-gray-400">Total Spare Types</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border border-green-500/20">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500/20 rounded-lg"><Wrench className="h-7 w-7 text-green-400" /></div>
                <div>
                  <p className="text-2xl font-bold text-white">{spares.filter(s => s.status === "Active").length}</p>
                  <p className="text-sm text-gray-400">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border border-red-500/20">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-500/20 rounded-lg"><Wrench className="h-7 w-7 text-red-400" /></div>
                <div>
                  <p className="text-2xl font-bold text-white">{spares.filter(s => s.status === "Discontinued").length}</p>
                  <p className="text-sm text-gray-400">Discontinued</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table Card */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader className="border-b border-gray-700 flex flex-row items-center justify-between">
            <CardTitle className="text-lg text-amber-400">Spares Catalog ({filtered.length})</CardTitle>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} placeholder="Search spares..." className="bg-gray-700 border-gray-600 text-white pl-10 w-64" data-testid="spares-db-search" />
              </div>
              <Button onClick={handleAddNew} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="add-spare-db-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Spare
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-700">
                  <TableHead className="text-amber-400">Spare ID</TableHead>
                  <TableHead className="text-amber-400">Spare Name</TableHead>
                  <TableHead className="text-amber-400">Category</TableHead>
                  <TableHead className="text-amber-400">Description</TableHead>
                  <TableHead className="text-amber-400 text-center">Default Qty</TableHead>
                  <TableHead className="text-amber-400 text-center">Unit</TableHead>
                  <TableHead className="text-amber-400 text-center">Status</TableHead>
                  <TableHead className="text-amber-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-gray-400 py-8">
                      {searchFilter ? "No matching spares found." : "No spares in database yet. Click 'Add Spare' to create one."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((spare) => (
                    <TableRow key={spare.id} className="border-gray-700">
                      <TableCell>
                        <span className="font-mono text-sm text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded border border-cyan-500/20" data-testid={`sdb-id-${spare.id}`}>
                          {spare.spare_id}
                        </span>
                      </TableCell>
                      <TableCell className="text-white font-medium">{spare.spare_name}</TableCell>
                      <TableCell className="text-gray-300">{spare.category || "-"}</TableCell>
                      <TableCell className="text-gray-300 max-w-[200px] truncate">{spare.description || "-"}</TableCell>
                      <TableCell className="text-center text-gray-300">{spare.default_quantity}</TableCell>
                      <TableCell className="text-center text-gray-300">{spare.unit}</TableCell>
                      <TableCell className="text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          spare.status === "Active" ? "bg-green-500/20 text-green-400 border border-green-500/30" :
                          "bg-red-500/20 text-red-400 border border-red-500/30"
                        }`} data-testid={`sdb-status-${spare.id}`}>
                          {spare.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(spare)} className="text-yellow-400 hover:text-yellow-300" data-testid={`sdb-edit-${spare.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(spare)} className="text-red-400 hover:text-red-300" data-testid={`sdb-delete-${spare.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md bg-gray-900 border-amber-500/50 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl text-amber-400 flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              {editingSpare ? "Edit Spare" : "Add New Spare"}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {editingSpare ? "Update the spare details" : "Add a new spare type to the database"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Spare Name *</Label>
              <Input value={formData.spare_name} onChange={(e) => setFormData(p => ({ ...p, spare_name: e.target.value }))} className="bg-gray-800 border-gray-600 text-white" placeholder="e.g., Bearing, Belt, Blade" data-testid="sdb-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Category</Label>
              <Input value={formData.category} onChange={(e) => setFormData(p => ({ ...p, category: e.target.value }))} className="bg-gray-800 border-gray-600 text-white" placeholder="e.g., Mechanical, Electrical" data-testid="sdb-category-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Description</Label>
              <Input value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} className="bg-gray-800 border-gray-600 text-white" placeholder="Optional description" data-testid="sdb-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Default Quantity</Label>
                <Input type="number" min="1" value={formData.default_quantity} onChange={(e) => setFormData(p => ({ ...p, default_quantity: parseInt(e.target.value) || 1 }))} className="bg-gray-800 border-gray-600 text-white" data-testid="sdb-qty-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Unit</Label>
                <select value={formData.unit} onChange={(e) => setFormData(p => ({ ...p, unit: e.target.value }))} className="w-full h-10 px-3 rounded-md bg-gray-800 border border-gray-600 text-white" data-testid="sdb-unit-select">
                  <option value="pcs">pcs</option>
                  <option value="set">set</option>
                  <option value="kg">kg</option>
                  <option value="meter">meter</option>
                  <option value="liter">liter</option>
                  <option value="roll">roll</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Status</Label>
              <select value={formData.status} onChange={(e) => setFormData(p => ({ ...p, status: e.target.value }))} className="w-full h-10 px-3 rounded-md bg-gray-800 border border-gray-600 text-white" data-testid="sdb-status-select">
                <option value="Active">Active</option>
                <option value="Discontinued">Discontinued</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="sdb-save-btn">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : editingSpare ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SparesDatabase;
